import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  RefreshCw, Eye, EyeOff, CheckCircle, AlertCircle, 
  ChevronRight, Mail, MessageSquare, Brain, KeyRound, ExternalLink
} from 'lucide-react';
import { SiTwilio, SiOpenai, SiGoogle } from 'react-icons/si';

// SendGrid doesn't have a react-icons entry, so we'll use Mail icon
const SiSendgrid = Mail;

// Helper to get auth config for axios requests
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// Integration configurations
const INTEGRATION_CONFIG = {
  email: {
    id: 'email',
    name: 'SendGrid',
    description: 'Transactional email delivery',
    icon: SiSendgrid,
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    linkTo: '/super-admin/email',
    category: 'communications'
  },
  sms: {
    id: 'sms',
    name: 'Twilio',
    description: 'SMS messaging & notifications',
    icon: SiTwilio,
    iconBg: 'bg-red-500/10',
    iconColor: 'text-red-400',
    linkTo: '/super-admin/sms',
    category: 'communications'
  },
  ai: {
    id: 'ai',
    name: 'OpenAI',
    description: 'AI-powered question generation',
    icon: SiOpenai,
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
    category: 'services'
  },
  auth: {
    id: 'auth',
    name: 'Google OAuth',
    description: 'Social login authentication',
    icon: SiGoogle,
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-400',
    category: 'services'
  }
};

export default function SuperAdminIntegrations() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [smsConfig, setSmsConfig] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadIntegrations();
  }, [user, navigate]);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const [integrationsRes, emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/platform/integrations`, getAuthConfig()),
        axios.get(`${API}/platform/email-config`, getAuthConfig()).catch(() => ({ data: null })),
        axios.get(`${API}/platform/sms-config`, getAuthConfig()).catch(() => ({ data: null }))
      ]);
      setIntegrations(integrationsRes.data);
      setEmailConfig(emailRes.data);
      setSmsConfig(smsRes.data);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureClick = (integration) => {
    setSelectedIntegration(integration);
    setConfigForm(integration.config || {});
    setShowConfigDialog(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedIntegration) return;
    try {
      await axios.patch(
        `${API}/platform/integrations/${selectedIntegration.id}`,
        { config: configForm, status: 'configured' },
        getAuthConfig()
      );
      toast.success('Integration configured successfully');
      setShowConfigDialog(false);
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const getStatusBadge = (isConfigured, isManaged = false) => {
    if (isManaged) {
      return (
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
          Managed
        </Badge>
      );
    }
    if (isConfigured) {
      return (
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
          Active
        </Badge>
      );
    }
    return (
      <Badge className="bg-zinc-700 text-zinc-400 border-zinc-600 text-[10px]">
        Setup Required
      </Badge>
    );
  };

  // Build integration cards data
  const communicationsIntegrations = [
    {
      ...INTEGRATION_CONFIG.email,
      isConfigured: emailConfig?.is_configured,
      hasLink: true
    },
    {
      ...INTEGRATION_CONFIG.sms,
      isConfigured: smsConfig?.enabled,
      hasLink: true
    }
  ];

  // Filter out SMS from API integrations (we show it above)
  const serviceIntegrations = integrations
    .filter(i => i.type !== 'sms')
    .map(i => ({
      ...INTEGRATION_CONFIG[i.type] || {
        name: i.name,
        description: i.description,
        icon: Brain,
        iconBg: 'bg-zinc-700',
        iconColor: 'text-zinc-400'
      },
      id: i.id,
      isConfigured: i.status === 'configured',
      isManaged: i.config?.managed_by === 'emergent',
      rawData: i
    }));

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="integrations-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Integrations</h1>
            <p className="text-sm text-zinc-500">Connect third-party services to your platform</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadIntegrations} 
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="refresh-integrations-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Communications Section */}
        <div className="space-y-3">
          <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Communications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {communicationsIntegrations.map((integration) => {
              const Icon = integration.icon;
              return (
                <Link 
                  key={integration.id} 
                  to={integration.linkTo}
                  data-testid={`integration-${integration.id}`}
                >
                  <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/80 transition-all cursor-pointer h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 ${integration.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-6 h-6 ${integration.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-zinc-100">{integration.name}</h3>
                            {getStatusBadge(integration.isConfigured)}
                          </div>
                          <p className="text-sm text-zinc-500">{integration.description}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Services Section */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            <span className="text-sm">Loading integrations...</span>
          </div>
        ) : serviceIntegrations.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceIntegrations.map((integration) => {
                const Icon = integration.icon;
                return (
                  <Card 
                    key={integration.id} 
                    className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all cursor-pointer"
                    onClick={() => handleConfigureClick(integration.rawData)}
                    data-testid={`integration-${integration.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 ${integration.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-6 h-6 ${integration.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-zinc-100">{integration.name}</h3>
                            {getStatusBadge(integration.isConfigured, integration.isManaged)}
                          </div>
                          <p className="text-sm text-zinc-500">{integration.description}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Info Footer */}
        <div className="flex items-start gap-3 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <AlertCircle className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-500">
            Platform integrations apply globally. Individual leagues can configure some settings from their admin panels.
          </p>
        </div>
      </div>

      {/* Configure Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">
              Configure {selectedIntegration?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {selectedIntegration?.config?.managed_by === 'emergent' ? (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-400 font-medium text-sm">Managed by Emergent</p>
                    <p className="text-emerald-500/70 text-xs">No configuration needed</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {selectedIntegration?.type === 'ai' && (
                  <div>
                    <Label className="text-zinc-300 text-sm">API Key</Label>
                    <div className="relative mt-2">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={configForm.api_key || ''}
                        onChange={(e) => setConfigForm({...configForm, api_key: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 pr-10 focus:border-amber-500 focus:ring-amber-500/20"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 h-7 w-7 p-0"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Uses Emergent LLM key by default</p>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="ghost" 
              onClick={() => setShowConfigDialog(false)} 
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            {selectedIntegration?.config?.managed_by !== 'emergent' && (
              <Button 
                onClick={handleSaveConfig} 
                className="bg-amber-500 hover:bg-amber-600 text-zinc-900"
              >
                Save Configuration
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
